import memoize from 'fast-memoize';
import { GenesisUserInfoResponse } from '../../types';
import ReactHtmlParser from 'react-html-parser';
import { keyBy } from 'lodash';
import {
  MultiOrgOption,
  OrgHierarchyResponse,
} from '../../hooks/useMultiOrgHierarchy';
import { flow, sortBy, uniqBy, map, filter, flatMap } from 'lodash/fp';

export const memoizedKeyBy = memoize(keyBy);

export const makeSet = memoize((array) => new Set(array));

export const isMultiOrgEnabled = (identity?: GenesisUserInfoResponse) =>
  identity?.permissions?.includes('permission.MultiOrg');

export const isMultiEntityEnabled = (identity?: GenesisUserInfoResponse) =>
  identity?.permissions?.includes('permission.MultiEntity');

export const highlighted = (text: string, keyword: string): string => {
  if (!keyword) return text;
  return ReactHtmlParser(
    text?.replace(new RegExp(keyword, 'ig'), '<b>$&</b>') || '',
  );
};

export const getAccessibleOrgs = (hierarchy?: OrgHierarchyResponse) => {
  if (!hierarchy?.userOrgIds?.length) return undefined;
  const userOrgIds = makeSet(hierarchy.userOrgIds);
  return hierarchy.options.filter((org) => userOrgIds.has(org.organizationId));
};

export type OrgTreeItem = MultiOrgOption & {
  selected: boolean;
  disabled: boolean;
};

export function decorateOrgsForTree(
  organizations: MultiOrgOption[],
  userOrgIds: string[],
  searchTerm: string,
  selectedOrgIds: string[],
): OrgTreeItem[] {
  const trimmedSearchTerm = searchTerm.trim().toLowerCase();
  const orgMap = memoizedKeyBy(organizations, 'organizationId');
  const userOrgIdSet = makeSet(userOrgIds);
  return flow(
    filter(
      (org: MultiOrgOption) =>
        org.organizationName.toLowerCase().includes(trimmedSearchTerm) &&
        userOrgIdSet.has(org.organizationId),
    ),
    flatMap((org: MultiOrgOption) => {
      const parents: MultiOrgOption[] = [org];
      let parentId = org.parentId;
      while (parentId) {
        const parent = orgMap[parentId];
        if (parent) parents.push(parent);
        parentId = parent?.parentId;
      }
      return parents;
    }),
    uniqBy('organizationId'),
    sortBy('organizationName'),
    map((org: MultiOrgOption) => ({
      ...org,
      selected: selectedOrgIds.includes(org.organizationId),
      disabled: !userOrgIdSet.has(org.organizationId),
    })),
  )(organizations);
}
